import {
  Directive,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { PermissionService } from '../services/permission.service';
import { PermissionAction } from '../models/permission.model';

/**
 * Directiva estructural que muestra el elemento solo si el usuario tiene el permiso indicado.
 *
 * Uso:
 *   <button *appHasPermission="'contacts:create'">Nuevo</button>
 *   <a *appHasPermission="'contacts'">...</a>   <!-- sin acción asume 'view' -->
 *
 * Default-deny: mientras la matriz no ha cargado, el elemento permanece oculto y se revela
 * cuando llega /api/me/permissions (reacciona a permissions$). Con el espejo en localStorage
 * normalmente ya está cargada en el primer render, evitando parpadeo.
 */
@Directive({
  selector: '[appHasPermission]',
  standalone: true,
})
export class HasPermissionDirective implements OnInit, OnDestroy {
  private moduleCode = '';
  private action: PermissionAction = 'view';
  private hasView = false;
  private sub?: Subscription;

  @Input('appHasPermission') set appHasPermission(value: string) {
    const [code, act] = (value ?? '').split(':');
    this.moduleCode = (code ?? '').trim();
    this.action = (act?.trim() as PermissionAction) || 'view';
    this.updateView();
  }

  constructor(
    private templateRef: TemplateRef<unknown>,
    private viewContainer: ViewContainerRef,
    private permissions: PermissionService,
  ) {}

  ngOnInit(): void {
    // Reevaluar cuando la matriz se carga/cambia (login, recarga async).
    this.sub = this.permissions.permissions$.subscribe(() => this.updateView());
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  private updateView(): void {
    const allowed = !!this.moduleCode && this.permissions.has(this.moduleCode, this.action);
    if (allowed && !this.hasView) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
    } else if (!allowed && this.hasView) {
      this.viewContainer.clear();
      this.hasView = false;
    }
  }
}
